import { observer } from "mobx-react-lite";
import { useAppStore } from "@/store";
import { useMemo } from "react";
import { NotesIndexTuple } from "@/store/note";
import { VirtualizedItemList } from "@/components/item-list";
import { ItemListRowData, ItemListRowType } from "@/components/item-list/types";
import { getItemListRowForNote } from "@/components/item-list/helpers";
import { groupLens, LensListItem } from "@/modules/timeline";
import { MDSText, MDSTextColor } from "@/mds";
import styled from "@emotion/styled";
import { itemsModule } from "@/modules/items";

export interface RecentsListProps {}

export const RecentsList = observer<RecentsListProps>(function RecentItems() {
  const { store, pageStore } = useAppStore();
  const state = pageStore.quickSearchModal;

  /**
   * We use state here [instead of observing the store] because we don't want to
   * update the UI while the recents UI while this component is open.
   *
   * (e.g. if someone selects a Note, we don't want the recent items list to update
   * while the list is fading out).
   */
  const recentItems = useMemo(() => {
    const recentItems = store.recentItems.sortedRecentNotesInteractedWithByMe;
    state.setOrderedItemIds(recentItems.map(note => note.id));
    return recentItems;

    /** This is intentional. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const itemRows: ItemListRowData[] = useMemo(() => {
    const slicedItems: LensListItem<NotesIndexTuple>[] = recentItems.slice(0, 100).map(note => ({
      dateTime: new Date(note.lastViewedAt),
      item: [1, note.lastViewedAt, note.id],
    }));

    if (slicedItems.length === 0) {
      return [
        {
          key: "header",
          type: ItemListRowType.SectionHeader,
          payload: { title: "Recent" },
        },
        {
          key: "empty-list",
          type: ItemListRowType.Other,
          payload: {
            children: (
              <EmptyListRowWrapper>
                <MDSText color={MDSTextColor.Tertiary}>No recently opened notes</MDSText>
              </EmptyListRowWrapper>
            ),
          },
        },
      ];
    }

    const groupedLensItems = groupLens(slicedItems);

    const output: ItemListRowData[] = [];
    for (const group of groupedLensItems) {
      output.push({
        type: ItemListRowType.SectionHeader,
        key: group.title,
        payload: { title: group.title },
      });

      for (const item of group.items) {
        const tuple = item.item;
        const itemId = tuple.length === 4 ? tuple[3] : tuple[2];
        const itemAppearance = itemsModule.resolveAppearance({ itemId, appStore: store });

        output.push({
          type: ItemListRowType.AsyncNote,
          key: itemId,
          payload: {
            noteId: itemId,
            itemRow: note =>
              getItemListRowForNote({
                dateTime: item.dateTime,
                inMainPanel: true,
                noteObservable: note,
                listState: state.listState,
                store,
                appearance: itemAppearance,
                disablePreview: true,
                trackClick: state.close,
              }),
          },
        });
      }
    }

    return output;
  }, [recentItems, state.listState, store, state.close]);

  return (
    <VirtualizedItemList
      rows={itemRows}
      listState={state.listState}
      scrollHighlightedItemIntoView
    />
  );
});

const EmptyListRowWrapper = styled.div`
  line-height: ${({ theme }) => theme.lineHeights["text-s"]};
  padding-inline: ${({ theme }) => theme.spacing["7"]};
  padding-block: ${({ theme }) => theme.spacing["4"]};
`;
